import { getAddress, formatEther } from 'viem';
import { BASE_URL } from './constants';

export const getNonce = async (address: string) => {
  if (!address) throw new Error('No address found');

  try {
    const response = await fetch(`${BASE_URL}/auth/nonce/${address}`);
    if (!response.ok) {
      console.error('Nonce fetch failed:', response.status, response.statusText);
      throw new Error('Failed to fetch nonce');
    }

    const data = await response.json();
    return data.nonce || data.message;
  } catch (error) {
    console.error('Error in getNonce:', error);
    throw error;
  }
};

export const createMessage = ({ nonce, address, chainId }: { nonce: string, address: string, chainId: number }) => {
  const domain = window.location.host;
  const origin = window.location.origin;
  const checksummedAddress = getAddress(address);

  const expirationTime = new Date();
  expirationTime.setHours(expirationTime.getHours() + 24);

  const issuedAt = new Date();

  const statement = "Please sign this message to verify your account ownership.";

  // Return the message as a plain string.
  // RainbowKit/Wagmi will use this string directly for signing.
  return `${domain} wants you to sign in with your Ethereum account:
${checksummedAddress}

${statement}

URI: ${origin}
Version: 1
Chain ID: ${chainId}
Nonce: ${nonce}
Issued At: ${issuedAt.toISOString()}
Expiration Time: ${expirationTime.toISOString()}`;
};

export const verify = async ({ message, signature, address }: { message: string, signature: string, address: string }) => {
  try {
    const verifyRes = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddress: address,
        signature,
        message,
      }),
    });

    if (!verifyRes.ok) {
      return false;
    }

    const data = await verifyRes.json();

    if (data.access_token && address) {
       localStorage.setItem('access_token', JSON.stringify({
          token: data.access_token,
          expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000,
          address: address
       }));
       return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const signOut = async () => {
  localStorage.removeItem('access_token');
};

export const getAccessToken = (expectedAddress?: string): string | null => {
  try {
    const stored = localStorage.getItem('access_token');
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    // Check if token is expired
    if (parsed.expiresAt && Date.now() > parsed.expiresAt) {
      localStorage.removeItem('access_token');
      return null;
    }

    // Check address match if provided
    if (expectedAddress && parsed.address && parsed.address.toLowerCase() !== expectedAddress.toLowerCase()) {
      return null;
    }

    return parsed.token || null;
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

// Backend response format
export interface LeaderboardEntry {
  rank: number;
  walletAddress: string;
  totalPoints?: string;
  tvl?: string;
  volume?: string;
  value?: string;
}

// Frontend usage format
export interface LeaderboardItem {
  rank: number;
  address: string;
  value: string | number;
}

export interface LeaderboardResponse {
  entries: LeaderboardEntry[];
  total?: number;
  page?: number;
  limit?: number;
}

export const getLeaderboard = async (
  page: number = 1,
  limit: number = 100,
  type?: string
): Promise<LeaderboardResponse> => {
  const token = getAccessToken();

  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (type) {
      params.append('type', type);
    }

    const url = `${BASE_URL}/users/leaderboard?${params.toString()}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        throw new Error('Authentication failed. Please sign in again.');
      }
      throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
    }

    const data = await response.json();
    //console.log('getLeaderboard response:', data);
    return data;
  } catch (error) {
    throw error;
  }
};

export interface UserRankMetric {
  value: string;
  rank: number | null;
}

export interface UserRankResponse {
  walletAddress: string;
  totalUsers: number;
  points: UserRankMetric;
  stakedApr: string;
  totalVolume: string;
  totalLiquidAprMon: string;
  totalLockedAprMon: string;
}

export interface UserRankInfo {
  walletAddress: string;
  totalUsers: number;
  points: UserRankMetric;
  tvl: UserRankMetric; // Keep for legacy compatibility if needed, or remove
  volume: UserRankMetric; // Keep for legacy compatibility if needed, or remove
}

export const getUserInfo = async (): Promise<UserRankResponse> => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. Please sign in first.');
  }

  try {
    const url = `${BASE_URL}/users/rank`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('access_token');
        throw new Error('Authentication failed. Please sign in again.');
      }
      throw new Error(`Failed to fetch user info: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      walletAddress: data.walletAddress,
      totalUsers: data.totalUsers,
      points: {
        value: data.points.value,
        rank: data.points.rank,
      },
      stakedApr: data.stakedApr ? formatEther(BigInt(data.stakedApr)) : '0',
      totalVolume: data.totalVolume || '0',
      totalLiquidAprMon: data.totalLiquidAprMon ? formatEther(BigInt(data.totalLiquidAprMon)) : '0',
      totalLockedAprMon: data.totalLockedAprMon ? formatEther(BigInt(data.totalLockedAprMon)) : '0',
    };
  } catch (error) {
    throw error;
  }
};
